import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Blocks, Plus, Edit3, Trash2, Download, X, ListChecks, Search } from 'lucide-react';
import logger from '@/lib/logger';

function emptyForm() {
  return { name: '', description: '', weight: 25, category: '', tasks: [] };
}

function PhaseBlocksTab() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const fetchBlocks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/phase-blocks');
      setBlocks(res.data || []);
    } catch (e) {
      logger.error('Error fetching phase blocks:', e);
      toast.error('Erro ao carregar blocos de fase');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBlocks(); }, [fetchBlocks]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return blocks;
    return blocks.filter((b) =>
      (b.name || '').toLowerCase().includes(q) ||
      (b.category || '').toLowerCase().includes(q) ||
      (b.description || '').toLowerCase().includes(q)
    );
  }, [blocks, search]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (block) => {
    setEditingId(block.block_id);
    setForm({
      name: block.name || '',
      description: block.description || '',
      weight: Number(block.weight) || 25,
      category: block.category || '',
      tasks: (block.tasks || []).map((t) => ({
        uid: crypto.randomUUID(),
        name: t.name,
        weight: Number(t.weight) || 1,
      })),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Informe o nome do bloco');
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description || null,
      weight: Number(form.weight) || 25,
      category: form.category || null,
      tasks: form.tasks
        .filter((t) => (t.name || '').trim())
        .map((t) => ({ name: t.name.trim(), weight: Number(t.weight) || 1 })),
    };
    try {
      if (editingId) {
        await api.put(`/phase-blocks/${editingId}`, payload);
        toast.success('Bloco atualizado');
      } else {
        await api.post('/phase-blocks', payload);
        toast.success('Bloco criado');
      }
      setModalOpen(false);
      fetchBlocks();
    } catch (e) {
      logger.error('Save block error:', e);
      toast.error('Erro ao salvar bloco');
    }
  };

  const handleDelete = async (block) => {
    if (!window.confirm(`Remover o bloco "${block.name}"?`)) return;
    try {
      await api.delete(`/phase-blocks/${block.block_id}`);
      toast.success('Bloco removido');
      fetchBlocks();
    } catch (e) {
      toast.error('Erro ao remover bloco');
    }
  };

  const handleImport = async () => {
    if (!window.confirm('Importar blocos a partir das fases existentes nos projetos? Nomes já existentes serão ignorados.')) return;
    setImporting(true);
    try {
      const res = await api.post('/phase-blocks/import-from-projects');
      toast.success(`${res.data.imported} bloco(s) importado(s)`);
      fetchBlocks();
    } catch (e) {
      logger.error('Import error:', e);
      toast.error('Erro ao importar blocos');
    } finally {
      setImporting(false);
    }
  };

  // Task list editing inside the modal (each row keyed by stable uid, not index,
  // so removing a row above doesn't steal focus from the input below)
  const addTask = () => {
    setForm((f) => ({
      ...f,
      tasks: [...f.tasks, { uid: crypto.randomUUID(), name: '', weight: 1 }],
    }));
  };
  const updateTask = (idx, patch) => {
    setForm((f) => {
      const tasks = f.tasks.slice();
      tasks[idx] = { ...tasks[idx], ...patch };
      return { ...f, tasks };
    });
  };
  const removeTask = (idx) => {
    setForm((f) => ({ ...f, tasks: f.tasks.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="space-y-4" data-testid="phase-blocks-panel">
      <Card className="p-4 border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Blocks className="w-5 h-5 text-emerald-700" />
              Blocos de Fase Reutilizáveis
            </h3>
            <p className="text-sm text-gray-500">
              Cada bloco é uma fase completa (nome + tarefas) que pode ser inserida em qualquer projeto.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleImport}
              disabled={importing}
              data-testid="phase-blocks-import-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {importing ? 'Importando…' : 'Importar das Fases existentes'}
            </Button>
            <Button onClick={openNew} className="bg-emerald-700 hover:bg-emerald-800" data-testid="phase-blocks-new-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Bloco
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-3 border border-gray-200">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar por nome, categoria ou descrição…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-0 focus-visible:ring-0 shadow-none"
            data-testid="phase-blocks-search"
          />
          <Badge variant="outline" className="ml-2">{filtered.length}/{blocks.length}</Badge>
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-800"></div>
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 border border-gray-200 text-center">
          <Blocks className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">
            {blocks.length === 0
              ? 'Nenhum bloco cadastrado. Comece importando das fases existentes ou criando um manualmente.'
              : 'Nenhum bloco corresponde à busca.'}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="phase-blocks-list">
          {filtered.map((b) => (
            <Card key={b.block_id} className="p-4 border border-gray-200 hover:shadow-md transition" data-testid={`phase-block-${b.block_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 break-words">{b.name}</p>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    {b.category && (
                      <Badge variant="outline" className="text-xs">{b.category}</Badge>
                    )}
                    {b.source === 'imported' && (
                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                        Importado
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                      Peso {b.weight}
                    </Badge>
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(b)} data-testid={`phase-block-edit-${b.block_id}`}>
                    <Edit3 className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(b)} data-testid={`phase-block-delete-${b.block_id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
              {b.description && <p className="text-xs text-gray-500 mb-2 break-words">{b.description}</p>}
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <ListChecks className="w-3.5 h-3.5" />
                {(b.tasks || []).length} tarefa(s)
              </div>
              {(b.tasks || []).length > 0 && (
                <ul className="mt-2 space-y-0.5 text-xs text-gray-600 max-h-32 overflow-auto pr-1">
                  {b.tasks.slice(0, 8).map((t, i) => (
                    <li key={`${t.name}-${i}`} className="truncate">• {t.name}</li>
                  ))}
                  {b.tasks.length > 8 && (
                    <li className="text-gray-400 italic">+ {b.tasks.length - 8} outras…</li>
                  )}
                </ul>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Bloco de Fase' : 'Novo Bloco de Fase'}</DialogTitle>
            <DialogDescription>
              Defina o nome da fase, descrição opcional, peso (% do projeto) e as tarefas que serão criadas ao aplicar o bloco.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nome da Fase</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex.: Fase I – Inventário Florestal"
                  data-testid="phase-block-form-name"
                />
              </div>
              <div>
                <Label>Categoria (opcional)</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Ex.: Licenciamento, Fauna"
                />
              </div>
              <div>
                <Label>Peso da Fase (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={form.weight}
                  onChange={(e) => setForm({ ...form, weight: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Descrição (opcional)</Label>
                <Textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Tarefas do Bloco ({form.tasks.length})</Label>
                <Button size="sm" variant="outline" onClick={addTask} data-testid="phase-block-add-task">
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Adicionar tarefa
                </Button>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {form.tasks.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Nenhuma tarefa adicionada. Use o botão acima.
                  </p>
                )}
                {form.tasks.map((t, idx) => (
                  <div key={t.uid || idx} className="flex gap-2 items-center" data-testid={`phase-block-task-row-${idx}`}>
                    <Input
                      className="flex-1"
                      placeholder="Nome da tarefa"
                      value={t.name}
                      onChange={(e) => updateTask(idx, { name: e.target.value })}
                    />
                    <Input
                      type="number"
                      className="w-24"
                      placeholder="Peso"
                      value={t.weight}
                      onChange={(e) => updateTask(idx, { weight: e.target.value })}
                    />
                    <Button size="sm" variant="ghost" onClick={() => removeTask(idx)}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={handleSave} data-testid="phase-block-save">
              {editingId ? 'Salvar Alterações' : 'Criar Bloco'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PhaseBlocksTab;
